import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import './OpportunitiesPage.css';

function OpportunitiesPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchLogs, setSearchLogs] = useState([]);
  const [activeQueries, setActiveQueries] = useState([]);

  useEffect(() => {
    fetchOpportunities();
    fetchActiveQueries();
  }, []);

  const fetchActiveQueries = async () => {
    try {
      const response = await fetch('/api/queries');
      const data = await response.json();
      if (data.success) {
        setActiveQueries(data.queries.filter(q => q.active));
      }
    } catch (err) {
      console.error("Failed to fetch active queries:", err);
    }
  };

  const runSearch = async () => {
    setSearching(true);
    // Add a separator for the new search instead of clearing logs
    setSearchLogs(prevLogs => [...prevLogs, `\n--- New Search Started at ${new Date().toLocaleTimeString()} ---`]);

    const eventSource = new EventSource('/api/trigger');

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'log') {
        setSearchLogs(prevLogs => [...prevLogs, data.message]);
      } else if (data.type === 'results') {
        setSearchLogs(prevLogs => [...prevLogs, `✅ Found ${data.count} new results!`]);
        fetchOpportunities();
      } else if (data.type === 'no_results') {
        setSearchLogs(prevLogs => [...prevLogs, '✅ Search finished. No new results found.']);
      } else if (data.type === 'done') {
        setSearchLogs(prevLogs => [...prevLogs, '🏁 Search complete.']);
        eventSource.close();
        setTimeout(() => setSearching(false), 2000);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err);
      setSearchLogs(prevLogs => [...prevLogs, '❌ An error occurred with the search stream.']);
      eventSource.close();
      setSearching(false);
    };
  };

  const handleCopyLogs = () => {
    const logText = searchLogs.join('\n');
    navigator.clipboard.writeText(logText)
      .then(() => alert('Logs copied to clipboard!'))
      .catch(err => console.error('Failed to copy logs: ', err));
  };

  const handleClearLogs = () => {
    setSearchLogs([]);
  };

  const resetQueries = async () => {
    if (!window.confirm('Reset to new improved search queries? This will clear old queries and use better customer-focused terms.')) {
      return;
    }

    try {
      const response = await fetch('/api/reset-queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSearchLogs([`✅ Queries reset! Now using ${data.data.newQueriesCount} improved queries.`]);
        fetchActiveQueries(); // Refresh the displayed queries
      } else {
        setSearchLogs([`❌ Reset failed: ${data.message}`]);
      }
    } catch (err) {
      setSearchLogs([`❌ Reset error: ${err.message}`]);
    }
  };

  const handleUpdateStatus = async (opportunityId, status) => {
    // Optimistically update the UI
    const originalOpportunities = [...opportunities];
    setOpportunities(opportunities.filter(op => op.id !== opportunityId));
    
    try {
      const response = await fetch('/api/opportunities', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId, status })
      });

      if (!response.ok) {
        // Revert on failure
        setOpportunities(originalOpportunities);
        const errorData = await response.json().catch(() => ({ message: 'Server error' }));
        setSearchLogs([`❌ Update failed: ${errorData.message}`]);
      }
    } catch (err) {
      // Revert on failure
      setOpportunities(originalOpportunities);
      setSearchLogs([`❌ Update failed: ${err.message}`]);
    }
  };

  const deleteOpportunity = async (opportunityId) => {
    // This is now just a specific case of updating status
    handleUpdateStatus(opportunityId, 'deleted');
  };

  const clearAllResults = async () => {
    if (!window.confirm('Are you sure you want to delete all search results?')) {
      return;
    }

    try {
      const response = await fetch('/api/opportunities', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setOpportunities([]);
        setSearchLogs(['✅ All results cleared']);
      } else {
        setSearchLogs([`❌ Clear failed: ${data.message}`]);
      }
      
    } catch (err) {
      console.error('Clear error:', err);
      setSearchLogs([`❌ Error: ${err.message}`]);
    }
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/opportunities');
      const data = await response.json();
      
      if (data.success) {
        const batches = data.opportunities || [];
        
        const flattened = batches.flatMap(batch => 
          (batch.results || []).flatMap(queryResult => 
            (queryResult.results || []).map(result => ({
              ...result,
              id: result.id || result.link,
              query: queryResult.query,
              batchTimestamp: batch.timestamp
            }))
          )
        );
        
        const uniqueOpportunities = Array.from(new Map(flattened.map(op => [op.id, op])).values());
        
        // Filter to only show new opportunities
        setOpportunities(uniqueOpportunities.filter(op => op.status === 'new' || !op.status));
      } else {
        setError(data.message || 'Failed to fetch opportunities');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    try {
      const now = new Date();
      const then = new Date(timestamp);
      const diffMs = now - then;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      return 'Invalid time';
    }
  };

  if (loading) {
    return (
      <div className="opportunities-page">
        <Navigation />
        <div className="container">
          <div className="loading-state">
            <h1>Loading...</h1>
            <p>Please wait while we fetch your results</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="opportunities-page">
        <Navigation />
        <div className="container">
          <div className="error-state">
            <h1>Error</h1>
            <p>{error}</p>
            <button onClick={fetchOpportunities} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunities-page">
      <Navigation />
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <h1>Reddit Opportunities</h1>
            <p>Find watch discussions about timing and accuracy issues</p>
          </div>
          <div className="header-actions">
            <button onClick={fetchOpportunities} className="refresh-btn" disabled={searching}>
              🔄 Refresh
            </button>
            {opportunities.length > 0 && (
              <button onClick={clearAllResults} className="clear-btn" disabled={searching}>
                🗑️ Clear All
              </button>
            )}
          </div>
        </div>

        <div className="search-section">
          <div className="search-header">
            <h2>Search Reddit</h2>
            <p>Click the button below to get a live feed of the search process.</p>
          </div>
          
          {activeQueries.length > 0 && !searching && (
            <div className="active-queries-list">
              <h4>Active Search Queries:</h4>
              <ul>
                {activeQueries.map(q => (
                  <li key={q.id}>
                    "{q.query}" (<em>{q.source}</em>)
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="search-controls">
            <button 
              onClick={runSearch} 
              disabled={searching}
              className={`search-btn ${searching ? 'searching' : ''}`}
            >
              {searching ? '🔍 Searching...' : '🔍 Search Reddit'}
            </button>
            <button 
              onClick={resetQueries} 
              disabled={searching}
              className="reset-queries-btn"
              style={{ marginLeft: '10px', backgroundColor: '#ff6b6b', color: 'white' }}
            >
              🔄 Reset Queries
            </button>
          </div>

          {searchLogs.length > 0 && (
            <div className="search-logs">
              <div className="search-logs-header">
                <h3>Search Logs</h3>
                <div className="log-actions">
                  <button onClick={handleCopyLogs} className="log-action-btn">Copy</button>
                  <button onClick={handleClearLogs} className="log-action-btn">Clear</button>
                </div>
              </div>
              <pre>
                {searchLogs.map((log, index) => (
                  <div key={index}>{log}</div>
                ))}
              </pre>
            </div>
          )}
        </div>

        {opportunities.length === 0 && !searching && (
          <div className="empty-state">
            <h2>No Results Yet</h2>
            <p>Click "Search Reddit" to find watch discussions!</p>
          </div>
        )}

        {opportunities.length > 0 && (
          <div className="results-section">
            <h2>Found {opportunities.length} Search Results</h2>
            <div className="results-list">
              {opportunities.map((result) => (
                <div key={result.id} className="result-item">
                  <div className="result-content">
                    <div className="result-title">
                      <a href={result.link || '#'} target="_blank" rel="noopener noreferrer">
                        {result.title || 'Watch Discussion Post'}
                      </a>
                    </div>
                    <div className="result-snippet">
                      {result.snippet || 'Click to view the full discussion'}
                    </div>
                    <div className="result-meta">
                      <span>r/{result.subreddit || 'unknown'}</span>
                      <span>Query: "{result.query || 'unknown'}"</span>
                      <span>{formatTimeAgo(result.timestamp)}</span>
                    </div>
                  </div>
                  <div className="result-actions">
                    <button 
                      onClick={() => handleUpdateStatus(result.id, 'reviewed')} 
                      className="reviewed-btn"
                      title="Mark as reviewed"
                    >
                      ✅
                    </button>
                    <button 
                      onClick={() => deleteOpportunity(result.id)} 
                      className="delete-btn"
                      title="Delete this item"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OpportunitiesPage; 